import styled from "styled-components";
import { ReactComponent as BackButton } from "assets/icons/icon-back-24.svg";
import { IconWrapper, PreviousIcon } from "common/components/icon";
import { useNavigate } from "react-router";
import {
  TextHeading3,
  TextTitle3,
} from "common/components/Style/new/textStyle";
import { colors } from "common/const/colors";
import { SideBarBackground } from "common/components";
import {
  HEADER_HEIGHT,
  PANEL_WIDTH,
  SIDEBAR_WIDTH_EXPANDED,
} from "common/const/const";
import { usePanel } from "common/utils/panelController";
import { slideIn, slideOut } from "common/components/Style/animations";
import { useEffect, useRef, useState } from "react";

const LayoutWrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: row;
  width: calc(100% - 3rem);
  gap: 1rem;
  margin: 1.5rem;
  box-sizing: border-box;
`;

export const ContentHeader = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  align-items: flex-end;
  justify-content: flex-start;
`;

const ContentArea = styled.div`
  display: flex;
  flex: 1;
  /* 내부 넓은 컨텐츠(예: FileList strip)가 박스를 밀어 넓히지 못하도록 */
  min-width: 0;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  background-color: var(--White);
  border-radius: var(--border-radius-16);
  /* 좌우 패딩은 ContentBody로 옮긴다. 하단 바(LayoutFooter)가 좌우 패딩에 갇히지
     않고 콘텐츠 영역 전체 폭을 쓰도록 하기 위함. 세로 패딩만 여기서 준다. */
  padding: ${({ $innerWidth }) => ($innerWidth ? "6rem" : "3.5rem")} 0;
  gap: ${({ gap }) => gap ?? "3rem"};
  box-sizing: border-box;
`;

// 헤더 + 본문(children)을 감싸 좌우 패딩을 담당. 푸터는 이 밖(ContentArea 직계)에 둔다.
const ContentBody = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: inherit;
  padding: 0 ${({ $padX }) => $padX};
  box-sizing: border-box;
`;

const LayoutFooter = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 2;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  gap: 1rem;
  /* 콘텐츠 영역 전체 폭을 차지. 좌우 패딩으로 내부 버튼은 본문과 정렬을 맞춘다.
     (ContentArea가 좌우 패딩을 없앴으므로 여기 %는 ContentBody와 동일 기준.) */
  width: 100%;
  padding: 1.5rem ${({ $padX }) => $padX};
  background: var(--White);
  /* 위쪽만 그림자 (음수 spread로 좌우/아래 번짐 제거). 맨 아래 스크롤 시 제거 */
  box-shadow: ${({ $flat }) =>
    $flat ? "none" : "0 -4px 6px -4px rgba(0, 0, 0, 0.12)"};
  transition: box-shadow 0.2s ease;
  box-sizing: border-box;
`;

const RightPanel = styled.aside`
  --anim-width: ${({ $width }) => $width};
  overflow: hidden;
  flex-shrink: 0;
  box-sizing: border-box;

  align-self: flex-start;
  height: calc(100vh - ${HEADER_HEIGHT} - 3rem);
  position: sticky;
  top: calc(${HEADER_HEIGHT} + 1.5rem);

  animation: ${({ $closing }) => ($closing ? slideOut : slideIn)} 0.3s ease-out
    forwards;
`;

const PanelInner = styled.div`
  width: ${({ $width }) => $width};
  height: 100%;
  padding: 2rem;
  box-sizing: border-box;
  overflow-y: auto;
  background-color: var(--White);
  border-radius: var(--border-radius-16);
`;

const DefaultLayout = ({
  title,
  subtitle,
  showBackButton = false,
  showLogo = false,
  innerWidth,
  gap,
  children,
  idName,
  titleRightElement,
  footer,
}) => {
  const navigate = useNavigate();
  const { panel, removePanel } = usePanel();
  const panelRef = useRef(null);

  // 하단 고정 바: 맨 아래 도달 시 그림자 제거 (스크롤 주체와 무관하게 뷰포트 기준)
  const [atBottom, setAtBottom] = useState(false);
  const footerSentinelRef = useRef(null);
  useEffect(() => {
    if (!footer) return;
    const el = footerSentinelRef.current;
    if (!el) return;
    const ob = new IntersectionObserver(
      ([entry]) => setAtBottom(entry.isIntersecting),
      { threshold: 0 },
    );
    ob.observe(el);
    return () => ob.disconnect();
  }, [footer]);

  useEffect(() => {
    if (panel && !panel.closing && panelRef.current) {
      const el = panelRef.current;
      let rafId;

      const follow = () => {
        window.scrollTo({ left: document.body.scrollWidth });
        rafId = requestAnimationFrame(follow);
      };

      rafId = requestAnimationFrame(follow);

      el.addEventListener("animationend", () => cancelAnimationFrame(rafId), {
        once: true,
      });
      return () => cancelAnimationFrame(rafId);
    }
  }, [panel]);

  const contentPadX = innerWidth ? `calc(100% - ${innerWidth})` : "3rem";

  return (
    <LayoutWrapper id={idName}>
      <SideBarBackground />
      <ContentArea $innerWidth={innerWidth} gap={gap}>
        <ContentBody $padX={contentPadX}>
          <ContentHeader>
            {showBackButton && (
              <IconWrapper
                onClick={() => navigate(-1)}
                style={{ marginRight: "1rem" }}>
                <PreviousIcon />
              </IconWrapper>
            )}
            {title && (
              <TextHeading3 style={{ marginRight: "0.5rem" }}>
                {title}
              </TextHeading3>
            )}
            {subtitle && (
              <TextTitle3 color={colors.LightNavy}>{subtitle}</TextTitle3>
            )}
            {titleRightElement && titleRightElement}
          </ContentHeader>
          {children}
        </ContentBody>
        {footer && (
          <>
            <LayoutFooter $flat={atBottom} $padX={contentPadX}>
              {footer}
            </LayoutFooter>
            <div
              ref={footerSentinelRef}
              aria-hidden
              style={{ height: 1, marginTop: "-1.5rem" }}
            />
          </>
        )}
      </ContentArea>
      {panel && (
        <RightPanel
          ref={panelRef}
          $width={panel.width || PANEL_WIDTH}
          $closing={panel.closing}
          onAnimationEnd={() => panel.closing && removePanel()}>
          <PanelInner $width={panel.width || PANEL_WIDTH}>
            {panel.content}
          </PanelInner>
        </RightPanel>
      )}
    </LayoutWrapper>
  );
};

const WithOutSideBarLayoutContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  min-height: 100vh;
  width: 100vw;

  overflow-y: auto;
`;

const WithOutSideBarLayout = ({
  isBackButton = true,
  isScroll = false,
  needBackground = false,
  children,
}) => {
  return (
    <WithOutSideBarLayoutContainer
      style={{
        height: isScroll ? "auto" : "100vh",
        justifyContent: isScroll ? "flex-start" : "center",
        backgroundImage: needBackground
          ? `url(${require("assets/images/worldmap.png")})`
          : "none",
        backgroundColor: colors.LightGray,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
      }}>
      {isBackButton && (
        <BackButton
          style={{
            position: "fixed",
            top: "2rem",
            left: "2rem",
            cursor: "pointer",
            zIndex: 100,
          }}
          onClick={() => {
            if (window.location.pathname === "/") {
              window.location.href = "/";
            } else window.history.back();
          }}
        />
      )}
      {children}
    </WithOutSideBarLayoutContainer>
  );
};

const Row = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: ${(props) => props.$align ?? "normal"};
  justify-content: ${(props) => props.$justify ?? "flex-start"};
  gap: ${(props) => props.gap ?? "1rem"};
  box-sizing: border-box;
`;

const Column = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: ${(props) => props.$align ?? "normal"};
  justify-content: ${(props) => props.$justify ?? "flex-start"};
  gap: ${(props) => props.gap ?? "1rem"};
  box-sizing: border-box;
`;

export { DefaultLayout, WithOutSideBarLayout, Row, Column };
